import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import api, { getList } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import Pager from "@/components/Pager";
import { inputCls, btnPrimary } from "@/components/Modal";
import { FeedbackForm } from "@/pages/ExperimentDetail";
import { useAuth } from "@/context/AuthContext";
import { FEEDBACK_SOURCES, PAGE_SIZE, fmtDate, label } from "@/lib/constants";

export default function Feedback() {
  const { canWrite } = useAuth();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({ source: "", experiment_id: "", date_from: "", date_to: "" });
  const [offset, setOffset] = useState(0);
  const [showForm, setShowForm] = useState(false);

  const { data: experiments = [] } = useQuery({
    queryKey: ["experiments-all"],
    queryFn: async () => (await api.get("/experiments", { params: { limit: 500 } })).data,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["feedback", filters, offset],
    queryFn: () => getList("/feedback", { ...filters, limit: PAGE_SIZE, offset }),
  });

  const entries = data?.items ?? [];
  const setFilter = (k) => (e) => { setOffset(0); setFilters({ ...filters, [k]: e.target.value }); };

  const { data: summary } = useQuery({
    queryKey: ["feedback-summary"],
    queryFn: async () => (await api.get("/feedback/summary")).data,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["feedback"] });
    queryClient.invalidateQueries({ queryKey: ["feedback-summary"] });
  };

  return (
    <div data-testid="feedback-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 pb-4 border-b border-[#E8DFD1]">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-[#1C100B]">Experience Centre Feedback</h1>
          <p className="text-sm text-[#8C7A6B] mt-1">Visitor, café and workshop responses.</p>
        </div>
        {canWrite && (
          <button onClick={() => setShowForm(true)} className={btnPrimary} data-testid="new-feedback-button">
            <Plus size={16} /> Add Feedback
          </button>
        )}
      </div>

      {summary && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-white border border-[#E8DFD1] rounded-xl p-6 shadow-sm" data-testid="avg-rating-card">
            <h2 className="font-display text-lg font-semibold text-[#1C100B] mb-4">Avg Rating per Experiment</h2>
            <div className="space-y-3">
              {summary.avg_per_experiment.map((r) => (
                <div key={r.experiment_id} data-testid={`avg-rating-${r.experiment_id}`}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-[#2A1810] font-medium truncate pr-3">{r.experiment_name}</span>
                    <span className="text-[#B08B46] font-bold shrink-0">★ {r.avg_rating} <span className="text-[#8C7A6B] font-normal">({r.count})</span></span>
                  </div>
                  <div className="h-1.5 bg-[#F4EFEA] rounded-full overflow-hidden">
                    <div className="h-full bg-[#C5A059] rounded-full" style={{ width: `${(r.avg_rating / 5) * 100}%` }} />
                  </div>
                </div>
              ))}
              {summary.avg_per_experiment.length === 0 && <p className="text-sm text-[#8C7A6B]">No ratings yet.</p>}
            </div>
          </div>
          <div className="bg-white border border-[#E8DFD1] rounded-xl p-6 shadow-sm" data-testid="tag-frequency-card">
            <h2 className="font-display text-lg font-semibold text-[#1C100B] mb-4">Tag Frequency</h2>
            <div className="flex flex-wrap gap-2">
              {summary.tag_frequency.map((t) => (
                <span key={t.tag} className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#F9F3E6] text-[#8A6D2F] border border-[#E5C992] px-3 py-1.5 rounded-full" data-testid={`tag-${t.tag.replace(/\s+/g, "-")}`}>
                  {t.tag}
                  <span className="bg-[#C5A059] text-[#2A1810] rounded-full px-1.5 py-0.5 text-[10px] font-extrabold">{t.count}</span>
                </span>
              ))}
              {summary.tag_frequency.length === 0 && <p className="text-sm text-[#8C7A6B]">No tags yet.</p>}
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-3 mb-6" data-testid="feedback-filters">
        <select data-testid="feedback-source-filter" value={filters.source} onChange={setFilter("source")} className={`${inputCls} w-52`}>
          <option value="">All sources</option>
          {FEEDBACK_SOURCES.map((s) => <option key={s} value={s}>{label(s)}</option>)}
        </select>
        <select data-testid="feedback-experiment-filter" value={filters.experiment_id} onChange={setFilter("experiment_id")} className={`${inputCls} w-64`}>
          <option value="">All experiments</option>
          {experiments.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
        <input data-testid="feedback-date-from" type="date" value={filters.date_from} onChange={setFilter("date_from")} className={`${inputCls} w-40`} />
        <input data-testid="feedback-date-to" type="date" value={filters.date_to} onChange={setFilter("date_to")} className={`${inputCls} w-40`} />
      </div>

      <div className="bg-white border border-[#E8DFD1] rounded-xl shadow-sm overflow-hidden" data-testid="feedback-table">
        <table className="w-full text-left text-sm">
          <thead className="bg-[#FAF6F0] border-b border-[#E8DFD1] text-[#2A1810] text-xs uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3.5 font-semibold">Date</th>
              <th className="px-4 py-3.5 font-semibold">Source</th>
              <th className="px-4 py-3.5 font-semibold">Experiment</th>
              <th className="px-4 py-3.5 font-semibold">Rating</th>
              <th className="px-4 py-3.5 font-semibold">Comment</th>
              <th className="px-4 py-3.5 font-semibold">Tags</th>
              {canWrite && <th className="px-4 py-3.5" />}
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#8C7A6B]">Loading…</td></tr>}
            {!isLoading && entries.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-[#8C7A6B]" data-testid="feedback-empty">No feedback matches these filters.</td></tr>
            )}
            {entries.map((f) => (
              <tr key={f.id} className="border-b border-[#F4EFEA] hover:bg-[#FDFBF7]" data-testid={`feedback-row-${f.id}`}>
                <td className="px-4 py-3.5 text-[#8C7A6B] whitespace-nowrap">{fmtDate(f.recorded_at)}</td>
                <td className="px-4 py-3.5"><StatusBadge value={f.source} /></td>
                <td className="px-4 py-3.5 text-[#2A1810] font-medium max-w-[180px] truncate">{f.experiment_name}</td>
                <td className="px-4 py-3.5">{f.rating != null ? <span className="font-bold text-[#B08B46]">★ {f.rating}</span> : <span className="text-[#8C7A6B]">—</span>}</td>
                <td className="px-4 py-3.5 text-[#5C4A3E] max-w-sm"><span className="line-clamp-2">{f.comment}</span></td>
                <td className="px-4 py-3.5">
                  <div className="flex gap-1 flex-wrap max-w-[160px]">
                    {(f.tags || []).map((t) => (
                      <span key={t} className="text-[10px] font-semibold bg-[#F9F3E6] text-[#8A6D2F] border border-[#E5C992] px-1.5 py-0.5 rounded">{t}</span>
                    ))}
                  </div>
                </td>
                {canWrite && (
                  <td className="px-4 py-3.5 text-right">
                    <button
                      onClick={async () => { await api.delete(`/feedback/${f.id}`); toast.success("Feedback deleted"); refresh(); }}
                      className="p-1.5 rounded hover:bg-[#FEF2F2] text-[#B91C1C]"
                      data-testid={`delete-feedback-${f.id}`}
                    >
                      <X size={14} />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        <Pager offset={offset} limit={PAGE_SIZE} total={data?.total ?? 0} onChange={setOffset} testid="feedback-pager" />
      </div>

      {showForm && (
        <FeedbackForm
          experimentId={filters.experiment_id || experiments[0]?.id}
          onClose={() => setShowForm(false)}
          onSaved={refresh}
        />
      )}
    </div>
  );
}
